// useTapActivate — ツールボタンの起動を `onClick` 依存から **pointer ベース** へ移す
// 共通フック (Classic `LegacyIconButton` / Modern `ModernToolRail` 共用)。
//
// 背景 (ペン / Windows Ink / Wacom での click 不発):
//   ブラウザの `click` は「同一要素で pointerdown → pointerup が成立」したときだけ
//   発火する。ところがペン + Windows Ink + Wacom 下では
//     (1) 軽い長押しが press-and-hold (右クリック扱い) になり pointercancel → click 取消、
//     (2) 接地/離地のジッタで down と up が別要素へ着地 (Classic のボタンは小さい)、
//     (3) バレル / 非プライマリで click 不発、
//   が起き、ツールボタン (消しゴム等) が「押しても切り替わらない」不発になる。
//   スポイト (右クリック) で実証・修正済みの同型問題 (`spuit.ts` / DrawingCanvas、
//   commit 21ae49a)。本フックはツールボタン側に同じ堅牢化を入れる。
//
// 方針:
//   - 起動は **onPointerUp** を主経路にする (onPointerDown は誤爆リスクがあり不適)。
//   - ガード: onPointerDown で「このボタン上で押下が始まった pointerId」を記録し、
//     onPointerUp が **プライマリ** (`isPrimary && button === 0`) かつ **同一ボタン上で
//     離された** ときだけ起動する。スクロール中のドラッグイン誤起動や、別要素から指/
//     ペンが滑ってきたケースを弾く。pointercancel / pointerleave で記録をリセット。
//   - キーボード (Enter / Space) は `onClick` を温存して維持する。ただし pointer 由来の
//     起動直後の click は二重発火なので **デデュープ** して skip する (= pointer を伴わ
//     ない click のみ通す)。スポイト fix と同じ設計。
//
// このモジュールの状態機械 (`createTapActivationState` + `tap*` 関数) は React/DOM 非依存
// の純ロジックとして切り出してあり、node 環境の vitest で単体テストできる
// (web は vitest node 環境のため、DOM イベント発火ではなくハンドラ関数を直接叩いて検証)。

import { useRef } from 'react';

/** onPointerUp / onPointerDown で参照する pointer イベントの最小形。 */
export interface PointerLike {
  readonly pointerId: number;
  readonly isPrimary: boolean;
  /** 離された (or 押された) ボタン。プライマリ左 / ペン先 = 0、バレル/右 = 2 等。 */
  readonly button: number;
  /** DOM event の timeStamp (page load 起点)。click とのデデュープ窓に使う。 */
  readonly timeStamp: number;
}

/** onClick で参照する click イベントの最小形。 */
export interface ClickLike {
  readonly timeStamp: number;
}

/**
 * pointer 起動 → click のデデュープ窓 (ms)。実機では pointerup → click は数 ms 差で
 * 連続発火するため、十分に余裕を持たせつつキーボード操作 (= 直前に pointer 起動が無い)
 * を巻き込まない値にする。consume (= 1 回 skip したらクリア) と併用するので、窓は
 * 「pointerup 後に click が来なかった取りこぼし」を一定時間で自然失効させる安全網。
 */
export const POINTER_CLICK_DEDUPE_WINDOW_MS = 700;

/**
 * pointerup が「ボタン起動」とみなせるプライマリ離地か。
 * プライマリ (`isPrimary`) かつ主ボタン (`button === 0`、左 / ペン先) のみ true。
 * バレル / 右クリック / 非プライマリは false (= 起動しない)。
 */
export const isPrimaryTapRelease = (e: Pick<PointerLike, 'isPrimary' | 'button'>): boolean =>
  e.isPrimary && e.button === 0;

/**
 * 直近の pointer 起動から窓内の click か (= 二重発火なので skip すべきか)。
 * `lastPointerActivationTs` が null (= pointer 起動が無い、典型的にはキーボード) なら false。
 */
export const shouldSuppressClickAfterPointer = (
  lastPointerActivationTs: number | null,
  now: number,
  windowMs: number = POINTER_CLICK_DEDUPE_WINDOW_MS,
): boolean => lastPointerActivationTs !== null && now - lastPointerActivationTs < windowMs;

/** タップ起動の状態 (1 ボタンにつき 1 つ、useRef で render 跨ぎに保持)。 */
export interface TapActivationState {
  /** このボタン上で押下が始まった pointerId。未押下 / リセット時は null。 */
  downPointerId: number | null;
  /** 直近で pointer 起動した時刻 (click デデュープ用)。未起動は null。 */
  lastPointerActivationTs: number | null;
}

export const createTapActivationState = (): TapActivationState => ({
  downPointerId: null,
  lastPointerActivationTs: null,
});

/** onPointerDown: このボタン上でプライマリ押下が始まったことを記録する。 */
export const tapPointerDown = (state: TapActivationState, e: PointerLike): void => {
  // プライマリのみ記録 (非プライマリ/バレルは起動対象外なので無視)。
  if (e.isPrimary) {
    state.downPointerId = e.pointerId;
  }
};

/**
 * onPointerUp: 同一ボタン上で始まったプライマリ離地なら起動 (true を返す)。
 * 起動時は click デデュープ用に起動時刻を記録する。押下記録は常にリセットする。
 */
export const tapPointerUp = (state: TapActivationState, e: PointerLike): boolean => {
  const startedHere = state.downPointerId === e.pointerId;
  state.downPointerId = null;
  if (startedHere && isPrimaryTapRelease(e)) {
    state.lastPointerActivationTs = e.timeStamp;
    return true;
  }
  return false;
};

/** onPointerCancel / onPointerLeave: 押下記録をリセット (ドラッグアウト等を起動させない)。 */
export const tapPointerReset = (state: TapActivationState): void => {
  state.downPointerId = null;
};

/**
 * onClick: キーボード (Enter/Space) 起動を通すべきか (true を返す)。
 * pointer 由来の起動直後 (窓内) の click は二重発火なので skip し、その記録を consume する
 * (= 1 回の pointer 起動につき最大 1 回の click を抑止。取りこぼしは窓で自然失効)。
 */
export const tapClick = (state: TapActivationState, e: ClickLike): boolean => {
  if (shouldSuppressClickAfterPointer(state.lastPointerActivationTs, e.timeStamp)) {
    state.lastPointerActivationTs = null;
    return false;
  }
  return true;
};

/** spread でボタン要素に渡すハンドラ束。 */
export interface TapHandlers {
  readonly onPointerDown: (e: React.PointerEvent) => void;
  readonly onPointerUp: (e: React.PointerEvent) => void;
  readonly onPointerCancel: (e: React.PointerEvent) => void;
  readonly onPointerLeave: (e: React.PointerEvent) => void;
  readonly onClick: (e: React.MouseEvent) => void;
}

/**
 * ツールボタン用タップ起動ハンドラ束を返す。`onActivate` が undefined (= 無効ボタン等)
 * のときは全ハンドラが no-op になる。挙動 (どのツールに切り替わるか等) は呼び出し側の
 * `onActivate` 次第で、本フックは **起動経路の堅牢化のみ** を担う。
 *
 * 使い方:
 *   const tap = useTapActivate(disabled ? undefined : onClick);
 *   <button type="button" {...tap} ... />  // onClick は tap が温存 (キーボード用)
 */
export const useTapActivate = (onActivate: (() => void) | undefined): TapHandlers => {
  // render 跨ぎで状態を保持。初回のみ生成 (lazy init)。
  const stateRef = useRef<TapActivationState | null>(null);
  if (stateRef.current === null) {
    stateRef.current = createTapActivationState();
  }
  const state = stateRef.current;

  return {
    onPointerDown: (e) => {
      if (onActivate) tapPointerDown(state, e);
    },
    onPointerUp: (e) => {
      if (onActivate && tapPointerUp(state, e)) onActivate();
    },
    onPointerCancel: () => {
      tapPointerReset(state);
    },
    onPointerLeave: () => {
      tapPointerReset(state);
    },
    onClick: (e) => {
      if (onActivate && tapClick(state, e)) onActivate();
    },
  };
};
